import React, { useEffect, useReducer, ReactElement } from "react";
import { connect } from "react-redux";
import { createSlice, PayloadAction } from "redux-starter-kit";
import EntityType from "../EntityType";
import { RootState } from "../../redux/rootReducer";
import AutoSuggest from "../AutoSuggest";
import { AutoSuggestItem } from "../AutoSuggestResponse";
import { useHttpRequest, LoadingState } from "../../http";
import GenericSuggestionsPickerProps from "./GenericSuggestionsPickerProps";
import { MetricsState } from "../../logging";
import SuggestionsPickerSelectedProps from "./suggestionsPickerSelected/SuggestionsPickerSelectedProps";

/**
 * Reducer state interface for suggestions picker.
 *
 * @interface SuggestionsPickerState
 */
interface SuggestionsPickerState {
  localSelectedId?: string;
  selectedSuggestion?: AutoSuggestItem;
  idToSuggestionMap: Map<string, AutoSuggestItem>;
  isCleared: boolean;
}

const initialState: SuggestionsPickerState = {
  localSelectedId: undefined,
  selectedSuggestion: undefined,
  idToSuggestionMap: new Map(),
  isCleared: false,
};

const suggestionsPickerSlice = createSlice({
  initialState,
  reducers: {
    setSelectedId: (state, action: PayloadAction<string | undefined>) => {
      state.localSelectedId = action.payload;
      state.selectedSuggestion = state.idToSuggestionMap.get(
        action.payload || ""
      );
    },
    setSelectedSuggestion: (
      state,
      action: PayloadAction<{ id: string; suggestion: AutoSuggestItem }>
    ) => {
      const { suggestion, id } = action.payload;

      if (!state.idToSuggestionMap.has(id)) {
        state.idToSuggestionMap = new Map(
          state.idToSuggestionMap.set(id, suggestion)
        );
      }

      state.selectedSuggestion = suggestion;
      state.isCleared = false;
    },
    clearSelection: (state) => {
      state.isCleared = true;
      state.selectedSuggestion = undefined;
    },
  },
});

const { reducer, actions } = suggestionsPickerSlice;

/**
 * React properties for suggestions picker component.
 *
 * @interface SuggestionsPickerProps
 * @extends GenericSuggestionsPickerProps
 */
export interface SuggestionsPickerProps extends GenericSuggestionsPickerProps {
  metrics: MetricsState;
  suggestionTypes: EntityType[];
  buildGetItemUrl: (urlId: string) => string;
  buildSuggestItem: (getItemResponse: any) => AutoSuggestItem;
  getIdFromSuggestItem: (suggestItem: AutoSuggestItem) => string;
  suggestionsSelectedComponent: React.FC<SuggestionsPickerSelectedProps>;
}

/**
 * Reusable suggestions picker component.
 *
 * @param {*} props
 * @returns {JSX.Element}
 */
const SuggestionsPicker: React.FC<SuggestionsPickerProps> = (
  props
): ReactElement => {
  const {
    onInputValueChange,
    suggestionOnClick,
    selectedId,
    disabled,
    clear,
    buildGetItemUrl,
    buildSuggestItem,
    getIdFromSuggestItem,
    suggestionsSelectedComponent,
    clearOnSelection,
    ...restOfProps
  } = props;

  const [state, dispatch] = useReducer<
    React.Reducer<SuggestionsPickerState, PayloadAction>
  >(reducer, {
    ...initialState,
    localSelectedId: selectedId,
  });

  const {
    loadingState,
    data: itemResponse,
    error,
    sendRequest,
  } = useHttpRequest("suggestionsPickerGetItem", restOfProps.metrics);

  useEffect(() => {
    // Changes propogate through changing selected id.
    // This updates the local selected id which changes the content.
    if (selectedId !== state.localSelectedId) {
      dispatch(actions.setSelectedId(selectedId));
    }
  }, [selectedId, state.localSelectedId]);

  useEffect(() => {
    if (
      state.localSelectedId &&
      loadingState !== LoadingState.LOADING &&
      !state.isCleared
    ) {
      const mappedValue = state.idToSuggestionMap.get(state.localSelectedId);
      if (mappedValue) {
        const id = getIdFromSuggestItem(mappedValue);
        dispatch(
          actions.setSelectedSuggestion({ id, suggestion: mappedValue })
        );
      } else if (buildGetItemUrl && loadingState === LoadingState.NO_DATA) {
        // Load from server.
        sendRequest({
          method: "POST",
          url: buildGetItemUrl(state.localSelectedId),
          data: { types: restOfProps.suggestionTypes },
        });
      }
    }
  }, [
    state.localSelectedId,
    state.idToSuggestionMap,
    state.isCleared,
    sendRequest,
    loadingState,
    buildGetItemUrl,
    getIdFromSuggestItem,
  ]);

  useEffect(() => {
    // Build suggest item using item response
    // This is for default value.
    if (
      itemResponse &&
      buildSuggestItem &&
      (itemResponse as any).data.length > 0
    ) {
      const suggestion = buildSuggestItem(
        (itemResponse as any).data[0].results[0]
      );
      const id = getIdFromSuggestItem(suggestion);
      dispatch(actions.setSelectedSuggestion({ id, suggestion }));
    }
  }, [itemResponse, buildSuggestItem, getIdFromSuggestItem]);

  const onSelect = (suggestion: AutoSuggestItem): void => {
    if (!clearOnSelection) {
      const id = getIdFromSuggestItem(suggestion);
      dispatch(actions.setSelectedSuggestion({ id, suggestion }));
    } else {
      dispatch(actions.clearSelection());
    }

    suggestionOnClick(suggestion);
  };

  const onClear = (): void => {
    dispatch(actions.clearSelection());
    if (clear !== undefined) {
      clear();
    }
  };

  const showSelected =
    disabled ||
    state.selectedSuggestion ||
    loadingState === LoadingState.LOADING;

  const SuggestionsSelected = suggestionsSelectedComponent;

  return (
    <React.Fragment>
      {!showSelected && (
        <AutoSuggest
          {...restOfProps}
          suggestionOnClick={onSelect}
          onInputValueChange={onInputValueChange}
        />
      )}
      {showSelected && (
        <SuggestionsSelected
          isSquare={restOfProps.isSquare}
          loadingState={loadingState}
          clearSelected={onClear}
          disabled={disabled}
          selectedItem={state.selectedSuggestion}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state: RootState): Pick<RootState, "metrics"> => ({
  metrics: state.metrics,
});

export default connect(mapStateToProps)(SuggestionsPicker);
